/**
 * LINE userId raw 表示の friendly fallback helper (PR-19 / F-11 / F-16)
 *
 * 背景:
 *   Dev UI audit F-11 / F-16 で LINE userId (`U` + 32 hex) が UI に raw 表示
 *   されていた。原因は BE `line-webhook.ts` が LINE Profile API 失敗時に
 *   `displayName = friendId` の fallback を発動し、その raw 値が以下に永続化
 *   されているため:
 *     - DynamoDB `notifications.title` (静的文字列、書き込み済データを救うには
 *       FE 描画層の置換が不可欠)
 *     - DynamoDB `lineFriends.displayName` (mutable、backfill 可だが既存 raw も
 *       FE 表示で先に救うのが UX 改善早道)
 *
 * 方針:
 *   - LINE userId 全量は UI に晒さない (個別識別可能性 + 視認性の両観点)
 *   - 末尾 4 桁のみ識別用に出す (`LINE ユーザー (末尾4桁: 7017)`)
 *   - 通常の displayName (山本 大輔 / 成山３ / 羅瑠♧laru 等) は誤検出せず素通し
 *   - 通知 title 内に **埋め込まれた** raw userId も部分置換
 *   - API call に使う raw friendId は本 helper を通さない (表示と分離)
 *
 * 表示言語 (p2-28): 各関数は省略可能な `locale` を受ける (既定は日本語 = 従来の文字列)。
 * 周りの文 (通知の title など BE が作った文) はデータなので訳さない。
 *
 * 同パターン参照:
 *   - PR-13 Group A `caseTypeLabel` (legacy alias 解決)
 *   - PR-14 Group B `vaultClientGroup.resolveVaultClientDisplayName` (UUID 抑制)
 */
import type { Locale } from '@/i18n/types';

/**
 * LINE userId の正規表現 (`U` プレフィックス + 32 hex 文字、大文字小文字混在受理)。
 *
 * 部分置換用に **non-global の case-insensitive** 形で定義。グローバル置換が
 * 必要な箇所では `new RegExp(LINE_USER_ID_PATTERN, 'gi')` で個別に作る。
 */
export const LINE_USER_ID_PATTERN: RegExp = /U[0-9a-f]{32}/i;

/** 未連携 / 完全に取得不可な LINE ユーザー向けの fallback */
export const LINE_USER_FALLBACK_UNLINKED = 'LINE ユーザー（未連携）';

/**
 * 英語表示の fallback (admin-english-toggle-20260924 phase 2 p2-28)。各関数の `locale` を省くと日本語
 * (従来の文字列)。この util は通知ドロワー (main chunk) からも使うので、画面の辞書を import せずに
 * 英語の 2 語だけをここに持つ (日本語は上の定数と下の組み立てが正本)。
 */
export const LINE_USER_FALLBACK_UNLINKED_EN = 'LINE user (not linked)';

/** 末尾 4 桁の表記 (表示言語別)。 */
function lineUserLast4Label(last4: string, locale: Locale): string {
  return locale === 'ja' ? `LINE ユーザー (末尾4桁: ${last4})` : `LINE user (last 4: ${last4})`;
}

function unlinkedLabel(locale: Locale): string {
  return locale === 'ja' ? LINE_USER_FALLBACK_UNLINKED : LINE_USER_FALLBACK_UNLINKED_EN;
}

/**
 * raw LINE userId から friendly fallback 文字列を組み立てる。
 *
 * 例: `'U35f5135e35fee8edb42496a14d357017'` → `'LINE ユーザー (末尾4桁: 7017)'`
 *
 * - 入力が LINE userId 形式に厳密一致しない場合は `LINE_USER_FALLBACK_UNLINKED`
 *   を返す (誤組立てしない、UI 露出ガード)
 * - 入力 null / undefined / 空文字 → `LINE_USER_FALLBACK_UNLINKED`
 * - 末尾 4 桁を抜き出すだけで全量は出力に含めない
 */
export function formatLineUserIdFallback(
  rawId: string | null | undefined,
  locale: Locale = 'ja',
): string {
  if (!rawId) return unlinkedLabel(locale);
  // exact match (^/$): 部分マッチで誤組立しない
  const exact = /^U[0-9a-f]{32}$/i;
  if (!exact.test(rawId)) return unlinkedLabel(locale);
  const last4 = rawId.slice(-4);
  return lineUserLast4Label(last4, locale);
}

/**
 * 文字列が LINE userId 形式の連続部分文字列を **どこかに含む**か判定する。
 *
 * 例: `'U35f...からLINEメッセージを受信しました'` → true
 *     `'山本 大輔'` → false
 */
export function containsLineUserId(text: string | null | undefined): boolean {
  if (!text) return false;
  return LINE_USER_ID_PATTERN.test(text);
}

/**
 * 表示用の文字列を解決する (PR-19 中核 API — LINE 友だち displayName 系)。
 *
 * 入力 value は **LINE 関連 field を想定** (`friend.displayName` 等)。
 * 「LINE 友だちの表示名としてどう出すか」のセマンティクス:
 *
 *   1. null / undefined / 空文字 → `LINE_USER_FALLBACK_UNLINKED`
 *      (LINE 関連で名前が無いなら "未連携" 扱い)
 *   2. 文字列全体が LINE userId 形式 → `formatLineUserIdFallback()` 経由 (末尾4桁)
 *   3. 文字列の **一部** に LINE userId が埋め込まれている (例: 通知 title)
 *      → 該当部分のみ `LINE ユーザー (末尾4桁: <last4>)` に置換、周辺の
 *        Japanese は維持
 *   4. それ以外 (通常の displayName / 任意の text) → そのまま素通し
 *
 * 注意: API call に使う raw friendId は本関数を通さない。本 helper は
 * **表示専用**で、操作系の identifier はコードベースで別 prop / variable に
 * 分離されている前提。
 */
export function resolveLineDisplayName(
  value: string | null | undefined,
  locale: Locale = 'ja',
): string {
  if (!value) return unlinkedLabel(locale);
  // 1. 全体一致 → 末尾4桁
  const exact = /^U[0-9a-f]{32}$/i;
  if (exact.test(value)) return formatLineUserIdFallback(value, locale);
  // 2. 部分含有 → embed 部分のみ置換 (グローバル replace)
  if (containsLineUserId(value)) {
    return value.replace(/U[0-9a-f]{32}/gi, (match) => formatLineUserIdFallback(match, locale));
  }
  // 3. 通常 string → 素通し
  return value;
}

/**
 * 汎用テキスト内の LINE userId をサニタイズする (PR-19、context 中立)。
 *
 * `resolveLineDisplayName` と異なり、**null / undefined / 空文字 / LINE userId
 * を含まない通常テキスト** はすべて元の値をそのまま返す (LINE 関連という
 * 前提を置かない)。raw LINE userId を含む場合のみ末尾4桁 fallback に置換。
 *
 * 用途: `notification.title` / `notification.message` / `notification.clientName`
 * のように、LINE 通知でも非 LINE 通知でも来る可能性のある汎用フィールドの
 * 描画前正規化。非 LINE 通知 (collection / case 通知等) で誤って "未連携"
 * fallback を出してしまうのを防ぐ。
 */
export function sanitizeLineUserIdInText(
  value: string | null | undefined,
  locale: Locale = 'ja',
): string {
  // null / undefined はそのまま透過 (React は何も描画しない)。
  // 戻り型は `string` 宣言だが、実装上は呼出側で React の {value} に直結する
  // 想定で null/undefined もそのまま流す (空文字 fallback を入れない)。
  if (value === null) return value as unknown as string;
  if (value === undefined) return value as unknown as string;
  if (value === '') return '';
  if (!containsLineUserId(value)) return value;
  return value.replace(/U[0-9a-f]{32}/gi, (match) => formatLineUserIdFallback(match, locale));
}
